/**
 * Anonymous builds publish to the community automatically. This module owns the
 * visitor's anonymous token and pushes builds to the community listing.
 *
 * Pushes happen on discrete events — a generation finishing, a build being
 * opened in the generator — not on every mutation. Per-keystroke syncing would
 * need a debounce, and a debounce needs a flush on page teardown, which mobile
 * browsers routinely kill mid-request. Event-driven pushes happen while the user
 * is sitting on the page, so they simply complete.
 *
 * The token is a bearer credential: whoever holds it owns the published builds,
 * exactly like a share slug. It lives beside the builds it authorises, so
 * clearing site data drops both together.
 *
 * Only the local (anonymous) path reaches here — buildStorage is never the
 * persistence layer for a logged-in user, whose builds go straight to the
 * authenticated build API instead.
 */
import { getActiveBuildId, getBuild, setBuildPublication } from './buildStorage';
import { deleteAnonBuild, syncAnonBuild } from './buildApi';
import { recordClientError } from './metrics';

// Of the 16 gear slots. A build below this floor is a draft, and auto-publishing
// drafts would bury real builds under freshly created blanks.
const MIN_FILLED_SLOTS = 8;
const KEY_ANON_TOKEN = 'dofgen_anon_token';
// Fingerprint of the last payload successfully pushed for each build.
//
// The push fires on discrete events, and "a build being opened" is one of them —
// so simply opening the same build repeatedly re-sent an identical payload every
// time. The upsert is idempotent, which made this invisible: the row was rewritten
// with its own contents. Measured on one ordinary day, the top visitor sent 282
// syncs having run zero generations, and hitting the per-minute ceiling was the
// only symptom. Publishing on change rather than on open is the actual fix; the
// rate limit was only ever counting the waste.
const KEY_SYNC_HASHES = 'dofgen_anon_sync';
// Entries kept before the map is dropped wholesale. Bounded so a visitor who
// makes hundreds of builds cannot grow localStorage without limit — clearing
// costs one redundant push per build, once.
const MAX_SYNC_HASHES = 200;

function randomToken() {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
    } catch (e) {
        // Older browsers without crypto.randomUUID fall through.
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/** The stored token, without creating one. */
export function readAnonToken() {
    try {
        return localStorage.getItem(KEY_ANON_TOKEN);
    } catch (e) {
        return null;
    }
}

/** The visitor's token, minted on first use so non-publishers never get one. */
export function getAnonToken() {
    try {
        let token = readAnonToken();
        if (!token) {
            token = randomToken();
            localStorage.setItem(KEY_ANON_TOKEN, token);
        }
        return token;
    } catch (e) {
        return null;
    }
}

/** djb2 over the payload — short, stable, and never leaves the browser. */
function fingerprint(payload) {
    const text = JSON.stringify(payload);
    let h = 5381;
    for (let i = 0; i < text.length; i += 1) h = (((h << 5) + h) ^ text.charCodeAt(i)) >>> 0;
    return `${h.toString(36)}:${text.length}`;
}

function readSyncHashes() {
    try {
        const raw = localStorage.getItem(KEY_SYNC_HASHES);
        const map = raw ? JSON.parse(raw) : null;
        return map && typeof map === 'object' ? map : {};
    } catch (e) {
        return {};
    }
}

function rememberSync(id, hash) {
    try {
        const map = readSyncHashes();
        if (Object.keys(map).length >= MAX_SYNC_HASHES) {
            localStorage.setItem(KEY_SYNC_HASHES, JSON.stringify({ [id]: hash }));
            return;
        }
        map[id] = hash;
        localStorage.setItem(KEY_SYNC_HASHES, JSON.stringify(map));
    } catch (e) {
        // A push that cannot be remembered is only pushed again next time.
    }
}

/** Drop a build's fingerprint so the next push always goes out. */
function forgetSync(id) {
    try {
        const map = readSyncHashes();
        if (!(id in map)) return;
        delete map[id];
        localStorage.setItem(KEY_SYNC_HASHES, JSON.stringify(map));
    } catch (e) {
        // Same as above: worst case is one redundant push.
    }
}

export function isPublishable(entry) {
    const items = entry?.build?.items;
    if (!Array.isArray(items)) return false;
    return items.filter(Boolean).length >= MIN_FILLED_SLOTS;
}

/**
 * Push one local build to the community listing. Idempotent: the stored
 * publicCode is replayed so the same row is updated rather than duplicated,
 * which is what makes it safe to call on every open.
 */
export async function pushAnonBuild(id) {
    if (!id) return;
    const entry = getBuild(id);
    if (!entry) return;
    // Nothing published and nothing worth publishing — stay off the network.
    if (!isPublishable(entry) && !entry.publicCode) return;

    const token = getAnonToken();
    if (!token) return;
    const payload = {
        token,
        publicCode: entry.publicCode || '',
        name: entry.name || '',
        build: entry.build || {},
        genConfig: entry.genConfig || {},
    };
    // Opening a build is one of the events that pushes, so without this the
    // same bytes go out again on every open. The community row already holds
    // them.
    //
    // Fingerprints the content only. `publicCode` is the row's identity, handed
    // back by the server on first publish — including it meant every new build
    // published twice, because the code the second push carried was the answer
    // to the first. A code going away is a retraction, and that clears the
    // fingerprint explicitly.
    const hash = fingerprint({
        name: payload.name,
        build: payload.build,
        genConfig: payload.genConfig,
    });
    if (readSyncHashes()[id] === hash) return;
    try {
        const data = await syncAnonBuild(payload);
        if (data?.published && data?.build) {
            setBuildPublication(id, {
                publicCode: data.build.publicCode || null,
                ownerSlug: data.build.owner?.public_slug || null,
            });
            // Only after the server confirms: remembering a push that failed
            // would strand the build unpublished until its next edit.
            rememberSync(id, hash);
        } else if (data && !data.published) {
            // Stripped back below the floor: the server retracted the row.
            setBuildPublication(id, { publicCode: null, ownerSlug: null });
            forgetSync(id);
        }
    } catch (e) {
        // Best effort. A failed push must never interrupt local editing; the
        // next event pushes again — but it is no longer silent, because a sync
        // that never succeeds is exactly the kind of failure that used to be
        // invisible. 429 excluded: that is the ceiling working, not a defect.
        forgetSync(id);
        if (e?.response?.status !== 429) {
            recordClientError('sync', e?.response?.status
                ? `sync-anon ${e.response.status}`
                : (e?.message || 'sync-anon failed'));
        }
    }
}

/** Push whichever build is currently open. */
export function pushActiveAnonBuild() {
    return pushAnonBuild(getActiveBuildId());
}

/** Retract a build from the community listing. */
export async function retractAnonBuild(entry) {
    const token = readAnonToken();
    if (!token || !entry?.publicCode) return;
    // Whatever happens next, the remembered fingerprint is now wrong: the row
    // is meant to be gone, so an unchanged build must be publishable again.
    if (entry?.id) forgetSync(entry.id);
    try {
        await deleteAnonBuild({ token, publicCode: entry.publicCode });
    } catch (e) {
        // Best effort, as above.
    }
}

/**
 * Retract everything this visitor has published. Used when local data is
 * cleared, which would otherwise orphan the published copies — the codes needed
 * to retract them live in the storage being wiped.
 */
export async function retractAllAnonBuilds(entries) {
    if (!Array.isArray(entries)) return;
    await Promise.all(entries.filter(entry => entry?.publicCode).map(retractAnonBuild));
}
